import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const writeVoicedrivenVideoScriptSkill: Skill = {
    name: SKILL_NAMES.writeVoicedrivenVideoScript,
    description:
        'How to write the script for a voiceover or talking-head video. Load before writing narration.',
    kind: 'howto',
    surfaces: ['sketch'],
    instructions: `# Writing a Voicedriven Video Script

## Speaking pace sets the budget
Narration runs at roughly **3.25 words per second** in English, so the target
duration is a hard word count:
30s: ~100 words
60s: ~195 words
90s: ~290 words

Duration is set by the words alone. When you have a plan for the video estimate
duration by adding up each section's word count and adjust accordingly. Generally
landing within about 5% of the target is close enough.

## Structure
- **No "Title" or "Introduction".** Narration should start in
  the substance; the title lives on screen, not in the voice. If a title is needed,
  add it as a text scene (no narration).
- Write with the build in mind. A sentence typically spans two or more overlays at
  speaking pace, so put natural clause breaks where those splits will land, and
  don't write sentences that can only be cut mid-phrase.

## Write it to be heard
The script is going through a voice, so the test is how it *sounds*, not how it
reads on a page.
- Aim for engaging, varied, articulate, and crisp. Vary sentence length so the
  narration has rhythm rather than a flat run of same-shaped sentences.
- **Spell words out in full** — "kilometers", not "km"; "percent", not "%". A voice
  reads what you wrote.
- **Introduce acronyms the way a person would say them**: "the Center for Disease
  Control, or the CDC," rather than "the Center for Disease Control (CDC)".
- Read it back in your head. Anything that trips the tongue will trip the voice.

## Avoid the tells
Generic narration openings are the fastest way to make a video feel machine-made.
- Don't open with dramatic boilerplate: "In a world…", "In the high-stakes world
  of…".
- Don't open with filler greetings: "Welcome to…", "Hey there!", "Imagine a…".
- Don't be more dramatic than the material warrants. Match the reference material's
  register rather than inflating it.

## Content fidelity
How faithful to be depends on whether the user gave you source material:
- **Summarizing a source** (an article, a document, a link): pull content from it and
  adapt freely — you are not bound to its structure — but do **not** add information,
  quotes, or data points that aren't in it. Do keep the specific details that make the
  story land, the real quotes and numbers, rather than paraphrasing them away. Cut
  whatever isn't needed for the story the user asked for.
- **Writing from a topic or brief** with no source: be creative and elaborate. When
  you're short on specifics, make a reasonable, informed guess rather than padding
  with generalities. **Be specific, not general** — a vague script is a worse failure
  than an imperfect detail.

In both cases:
- Weave any additional context the user supplied in naturally.
- Match the **tone** of the reference material unless the user asked for something
  else, and match its **perspective** (first vs. third person) the same way.
- Write in the language of the reference material unless told otherwise.

## Formatting rules
- **Never put an em dash (—) in the script.** Write two sentences, or join with a
  comma or a conjunction.
- Plain text only: the words you write are the words that get spoken, so no speaker
  labels ("Narrator:"), no emphasis marks, no stage directions ("[shout]"), no
  markdown, and no emojis.`,
};
